import { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tag, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * SupplierCategoriesPage — gestion CRUD des catégories de fournisseurs.
 * Catégories par défaut seedées automatiquement à la première visite.
 */
const SupplierCategoriesPage = () => {
  const { isOrgAdmin, isSuperAdmin } = useOrg();
  const canEdit = isOrgAdmin || isSuperAdmin;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ name: "", color: "#6366f1", sort_order: 100 });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await api.getSupplierCategories();
      setItems(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const openCreate = () => {
    setForm({ name: "", color: "#6366f1", sort_order: 100 });
    setDialog({ open: true, editing: null });
  };

  const openEdit = (cat) => {
    setForm({ name: cat.name, color: cat.color || "#6366f1", sort_order: cat.sort_order ?? 100 });
    setDialog({ open: true, editing: cat });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (dialog.editing) {
        await api.updateSupplierCategory(dialog.editing.id, form);
        toast.success("Catégorie mise à jour");
      } else {
        await api.createSupplierCategory(form);
        toast.success("Catégorie créée");
      }
      setDialog({ open: false, editing: null });
      reload();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteSupplierCategory(deleteTarget.id);
      toast.success(`Catégorie "${deleteTarget.name}" supprimée`);
      setDeleteTarget(null);
      reload();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de suppression"));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4 max-w-4xl" data-testid="supplier-categories-page">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              Catégories fournisseurs
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Triez et filtrez vos fournisseurs (matières premières, services, loueurs, etc.).
            </p>
          </div>
          {canEdit && (
            <Button onClick={openCreate} data-testid="create-category-btn">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle catégorie
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Aucune catégorie. Créez-en une pour démarrer.</p>
          ) : (
            <div className="divide-y border rounded-md">
              {items.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center justify-between p-3 hover:bg-muted/40"
                  data-testid={`category-row-${cat.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full flex-none"
                      style={{ background: cat.color || "#6366f1" }}
                    />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{cat.name}</div>
                      <div className="text-xs text-muted-foreground">
                        Ordre {cat.sort_order ?? "—"}
                        {cat.is_default && <span className="ml-2 text-emerald-700">• par défaut</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">{cat.suppliers_count} fournisseur(s)</Badge>
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(cat)} data-testid={`edit-category-${cat.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(cat)}
                          data-testid={`delete-category-${cat.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, editing: null })}>
        <DialogContent data-testid="category-edit-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
            <DialogDescription>Donnez un nom clair (ex : Matières premières, Loueurs…).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Couleur</Label>
                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="category-color-input" />
              </div>
              <div>
                <Label>Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 100 })}
                  data-testid="category-sort-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-category-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {dialog.editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la catégorie ?</AlertDialogTitle>
            <AlertDialogDescription>
              La catégorie « {deleteTarget?.name} » sera supprimée. Les fournisseurs liés
              ne seront pas supprimés mais perdront leur catégorie (à ré-affecter manuellement).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-category-btn">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SupplierCategoriesPage;
